import { useState } from 'react';
import toast from '@/components/atoms/toaster/helpers/toast';
import { useTranslations } from 'use-intl';
import { isCsvUploadFile } from '@/utils/file/accepted-upload-file';

const useCSVFileHandler = () => {
  const translate = useTranslations();
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<Record<string, string>>({});
  const [readFiles, setReadFiles] = useState<Record<string, string>>({});
  const [loadingProgress, setLoadingProgress] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});

  const handleFile = (inputFiles: File[]) => {
    const newFileError = { ...fileError };
    const resultFiles: File[] = [];
    inputFiles.forEach((inputFile) => {
      if (files.find((file) => file.name === inputFile.name && file.type === inputFile.type)) {
        toast.error(
          translate('quick-order.error-file-exists', {
            file: inputFile.name,
          }),
        );
      } else {
        // EWM-949 — a MIME-only check rejected legitimate files: Windows and
        // several browsers report `application/vnd.ms-excel` (and sometimes
        // nothing at all) for a plain .csv, so the extension has to count.
        // Round 1 (R1-5): the gate stays CSV-ONLY here. This panel reads the
        // file as text and parses the rows itself, so an .xls/.xlsx is a binary
        // blob it can never read — admitting it only delays the failure and
        // swaps the CSV-specific message for the generic one.
        if (!isCsvUploadFile(inputFile)) newFileError[inputFile.name] = translate('quick-order.error-format-file');

        resultFiles.push(inputFile);
      }
    });
    setFileError(newFileError);
    setFiles((array) => [...resultFiles, ...array]);
  };

  const handleFileReader = (files: File[]) => {
    files.forEach((file) => {
      if (!fileError[file.name]) {
        const reader = new FileReader();

        // EWM-949 — this whole body used to run unguarded:
        //
        //   (reader.result as string).split(',')[1].toLowerCase()
        //
        // `split(',')[1]` is `undefined` for a file with no comma ANYWHERE, so
        // it threw a TypeError inside `onload`. Nothing catches a throw in an
        // async FileReader callback, so `setFileError` never ran, the panel's
        // loading state was never cleared, and the customer was left mid-load
        // with no feedback at all. Guarded index + try/catch/finally, so every
        // failure surfaces an error and loading is ALWAYS cleared.
        reader.onload = () => {
          try {
            const content = String(reader.result ?? '');
            // The check reads the HEADER ROW, not the whole file split on
            // commas. Reading the whole file meant "sku"/"quantity" appearing
            // anywhere in the BODY passed the check (including under a title
            // row this panel then parses as a product line), while a perfectly
            // good header could fail it.
            const headerRow = content.split(/\r?\n/).find((line) => line.trim().length > 0) ?? '';
            const headerCells = headerRow.split(',');
            // This panel's row parser (useProductHandler) is comma-only, so a
            // header we cannot split into two comma cells is a file it cannot
            // read — say so, rather than accept it and yield nothing.
            const includesSKU = (headerCells[0] ?? '').toLowerCase().includes('sku');
            const includesQuantity = (headerCells[1] ?? '').toLowerCase().includes('quantity');

            if (content.split('\n').length > 501)
              setFileError((fileError) => ({ ...fileError, [file.name]: translate('quick-order.error-file-exceeds') }));
            setReadFiles((readFiles) => ({ ...readFiles, [file.name]: content }));
            if (!includesSKU || !includesQuantity)
              setFileError((fileError) => ({ ...fileError, [file.name]: translate('quick-order.error-format') }));
          } catch {
            setFileError((fileError) => ({ ...fileError, [file.name]: translate('quick-order.error-upload-fail') }));
          } finally {
            setLoading((loading) => ({ ...loading, [file.name]: false }));
          }
        };
        reader.onprogress = (e) => {
          setLoading((loading) => ({ ...loading, [file.name]: true }));
          setLoadingProgress((loadingProgress) => ({ ...loadingProgress, [file.name]: (e.loaded / e.total) * 100 }));
        };
        reader.onerror = () => {
          setFileError((fileError) => ({ ...fileError, [file.name]: translate('quick-order.error-upload-fail') }));
        };
        reader.readAsText(file);
      }
    });
  };

  const handleClearFiles = () => {
    setFiles([]);
  };

  const handleClearReadFiles = () => {
    setReadFiles({});
  };

  const handleRemoveClick = (removedFile: File) => {
    if (Object.keys(fileError).length > 0) {
      const newFileError = { ...fileError };
      delete newFileError[removedFile.name];
      setFileError(newFileError);
    }
    if (Object.keys(readFiles).length > 0) {
      const newReadFiles = { ...readFiles };
      delete newReadFiles[removedFile.name];
      setReadFiles(newReadFiles);
    }
    setFiles((files) => files.filter((file) => file.name != removedFile.name));
  };

  return {
    files,
    readFiles,
    fileError,
    loadingProgress,
    loading,
    handleFile,
    handleFileReader,
    handleClearFiles,
    handleRemoveClick,
    handleClearReadFiles,
  };
};
export default useCSVFileHandler;
